/**
 * Outreach Sender Accounts page.
 */
import React, { useEffect, useState } from 'react';
import { Table, Card, Tag, Space, Typography, Button, Modal, Input, InputNumber, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { SenderAccount } from '../types';
import { outreachSenderAccountsApi } from '../api';

const { Text } = Typography;

/**
 * A small component that shows a countdown until the reset time.
 * It updates every second.
 */
const ResetTimer: React.FC<{ resetAt: string | null; currentSends: number; onReset?: () => void }> = ({ resetAt, currentSends, onReset }) => {
  const [timeLeft, setTimeLeft] = useState<string | null>(null);

  useEffect(() => {
    let resetTriggered = false;
    const calculateTimeLeft = () => {
      if (!resetAt) {
        // If no reset time but there are sends, show a message
        if (currentSends > 0) {
          setTimeLeft('Reset time pending...');
        } else {
          setTimeLeft(null);
        }
        return;
      }
      
      const resetDate = new Date(resetAt);
      const now = new Date();
      const diffMs = resetDate.getTime() - now.getTime();
      
      if (diffMs <= 0) {
        setTimeLeft('Reset pending');
        if (onReset && !resetTriggered) {
          resetTriggered = true;
          // Delay the refresh slightly to allow backend to process
          setTimeout(() => {
            onReset();
          }, 2000);
        }
        return;
      }
      
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const diffSecs = Math.floor((diffMs % (1000 * 60)) / 1000);
      
      if (diffHours > 0) {
        setTimeLeft(`Resets in ${diffHours}h ${diffMins}m`);
      } else if (diffMins > 0) {
        setTimeLeft(`Resets in ${diffMins}m ${diffSecs}s`);
      } else {
        setTimeLeft(`Resets in ${diffSecs}s`);
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [resetAt, currentSends, onReset]);

  if (!timeLeft) return null;
  return <Text type="secondary" style={{ fontSize: 13, fontWeight: 500, display: 'block', color: '#666' }}>{timeLeft}</Text>;
};

export const OutreachSenderAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<SenderAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [footerModalVisible, setFooterModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState<SenderAccount | null>(null);
  const [footerText, setFooterText] = useState('');
  const [saving, setSaving] = useState(false);
  const [limitModalVisible, setLimitModalVisible] = useState(false);
  const [editingLimitAccount, setEditingLimitAccount] = useState<SenderAccount | null>(null);
  const [dailyLimit, setDailyLimit] = useState<number>(100);
  const [savingLimit, setSavingLimit] = useState(false);

  const fetchAccounts = () => {
    setLoading(true);
    outreachSenderAccountsApi.list()
      .then(res => setAccounts(res.items))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAccounts(); }, []);

  const handleEditFooter = (account: SenderAccount) => {
    setEditingAccount(account);
    setFooterText(account.footer || '');
    setFooterModalVisible(true);
  };

  const handleSaveFooter = async () => {
    if (!editingAccount) return;
    setSaving(true);
    try {
      await outreachSenderAccountsApi.updateFooter(editingAccount.id, { footer: footerText });
      message.success('Footer updated successfully');
      setFooterModalVisible(false);
      fetchAccounts();
    } catch {
      message.error('Failed to update footer');
    } finally {
      setSaving(false);
    }
  };

  const handleEditDailyLimit = (account: SenderAccount) => {
    setEditingLimitAccount(account);
    setDailyLimit(account.daily_send_limit);
    setLimitModalVisible(true);
  };

  const handleSaveDailyLimit = async () => {
    if (!editingLimitAccount) return;
    if (dailyLimit < 1) {
      message.warning('Daily limit must be at least 1');
      return;
    }
    setSavingLimit(true);
    try {
      await outreachSenderAccountsApi.updateDailyLimit(editingLimitAccount.id, { daily_send_limit: dailyLimit });
      message.success('Daily limit updated successfully');
      setLimitModalVisible(false);
      fetchAccounts();
    } catch {
      message.error('Failed to update daily limit');
    } finally {
      setSavingLimit(false);
    }
  };

  const columns: ColumnsType<SenderAccount> = [
    { title: 'Account Key', dataIndex: 'account_key', key: 'account_key' },
    { title: 'Display Name', dataIndex: 'display_name', key: 'display_name', render: (n: string | null) => n || '-' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { 
      title: 'Current Sends / Daily Limit', 
      key: 'daily_limit_sends',
      render: (_: any, r: SenderAccount) => {
        const pct = (r.current_daily_sends / r.daily_send_limit) * 100;
        const color = pct >= 90 ? 'red' : pct >= 70 ? 'orange' : 'green';
        return (
          <Space direction="vertical" size={4}>
            <Space>
              <Tag color={color}>{r.current_daily_sends} / {r.daily_send_limit}</Tag>
              <Button 
                type="link" 
                size="small" 
                icon={<EditOutlined />} 
                onClick={() => handleEditDailyLimit(r)}
                style={{ padding: 0, height: 'auto' }}
              >
                Edit
              </Button>
            </Space>
            <ResetTimer resetAt={r.daily_limit_reset_at} currentSends={r.current_daily_sends} onReset={fetchAccounts} />
          </Space>
        );
      }
    },
    { title: 'Footer / Signature', key: 'footer',
      render: (_, r: SenderAccount) => (
        <span style={{ whiteSpace: 'pre-line', fontSize: 12, color: '#555' }}>
          {r.footer ? (r.footer.length > 60 ? r.footer.substring(0, 60) + '...' : r.footer) : <Text type="secondary">Not set</Text>}
        </span>
      ),
    },
    { title: 'Actions', key: 'actions',
      render: (_, r: SenderAccount) => (
        <Button type="link" icon={<EditOutlined />} onClick={() => handleEditFooter(r)}>Edit Footer</Button>
      ),
    },
    { title: 'Status', key: 'status', render: (_, r: SenderAccount) => (
      <Space>
        {r.is_active ? <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag> : <Tag icon={<CloseCircleOutlined />}>Inactive</Tag>}
        {r.is_verified ? <Tag color="blue">Verified</Tag> : <Tag color="orange">Unverified</Tag>}
      </Space>
    )},
    { title: 'Last Used', dataIndex: 'last_used_at', key: 'last_used_at',
      render: (d: string | null) => d ? new Date(d).toLocaleString() : '-' },
  ];

  return (
    <div>
      <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Table columns={columns} dataSource={accounts} loading={loading} rowKey="id" pagination={false} />
      </Card>

      <Modal
        title={`Edit Footer — ${editingAccount?.display_name || editingAccount?.account_key || ''}`}
        open={footerModalVisible}
        onCancel={() => setFooterModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setFooterModalVisible(false)} disabled={saving}>Cancel</Button>,
          <Button key="save" type="primary" onClick={handleSaveFooter} loading={saving}>Save Footer</Button>,
        ]}
        width={600}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          This footer/signature is automatically appended to every email sent from this account.
          Use line breaks for formatting (e.g. name on one line, company on the next).
        </Text>
        <Input.TextArea
          rows={6}
          value={footerText}
          onChange={e => setFooterText(e.target.value)}
          placeholder={"Best Regards,\nJohn Doe\nSNS Intelligence"}
        />
        {footerText && (
          <div style={{ marginTop: 16, padding: 12, background: '#f5f7fa', borderRadius: 6 }}>
            <Text strong style={{ fontSize: 12 }}>Preview:</Text>
            <div style={{ whiteSpace: 'pre-line', marginTop: 4, fontSize: 13 }}>{footerText}</div>
          </div>
        )}
      </Modal>

      <Modal
        title={`Edit Daily Limit — ${editingLimitAccount?.display_name || editingLimitAccount?.account_key || ''}`}
        open={limitModalVisible}
        onCancel={() => setLimitModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setLimitModalVisible(false)} disabled={savingLimit}>Cancel</Button>,
          <Button key="save" type="primary" onClick={handleSaveDailyLimit} loading={savingLimit}>Save Limit</Button>,
        ]}
        width={500}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Set the maximum number of emails this account can send per day.
        </Text>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Daily Send Limit:</Text>
          <InputNumber
            min={1}
            max={10000}
            value={dailyLimit}
            onChange={(v) => setDailyLimit(v || 100)}
            style={{ width: '100%' }}
          />
        </div>
        {editingLimitAccount && (
          <div style={{ marginTop: 16, padding: 12, background: '#f5f7fa', borderRadius: 6 }}>
            <Text strong style={{ fontSize: 12 }}>Current Status:</Text>
            <div style={{ marginTop: 4 }}>
              <Text>Current sends today: </Text>
              <Text strong>{editingLimitAccount.current_daily_sends} / {editingLimitAccount.daily_send_limit}</Text>
            </div>
            {editingLimitAccount.daily_limit_reset_at && (
              <div style={{ marginTop: 4 }}>
                <Text>Limit resets: </Text>
                <Text strong>{new Date(editingLimitAccount.daily_limit_reset_at).toLocaleString()}</Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};
